import React, { useMemo, useState } from "react";
import { SettingsHeader } from "../index";
import { Button, Modal, SettingsPanel, Spinner, Select } from "../../../shared";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks/useConfig";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  useCreateTaskTemplateGroupMutation,
  useDeleteTaskTemplateGroupMutation,
  useGetTaskTemplateGroupsQuery,
  useUpdateTaskTemplateGroupMutation,
} from "../../../redux/services/tasks/tasksApi";
import { useGetTaskTemplatesQuery } from "../../../redux/services/taskTemplates/api";
import type {
  ITaskTemplate,
  ITaskTemplateGroup,
  TaskTemplateAppliesTo,
  TaskTemplateAppType,
  TaskTemplateIndustry,
  TaskTemplateScope,
} from "../../../types";
import {
  PencilIcon,
  TrashIcon,
  Squares2X2Icon,
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

type GroupDraft = {
  _id?: string;
  groupKey: string;
  title: string;
  description: string;
  scope: TaskTemplateScope;
  appType: TaskTemplateAppType;
  industry: TaskTemplateIndustry;
  appliesTo: TaskTemplateAppliesTo;
  active: boolean;
  autoApply: boolean;
  autoApplyMode: "dedupe" | "override";
  triggerRules: Array<{
    event: "leg_created" | "leg_assigned" | "leg_status";
    status?: string;
  }>;
  templates: Array<{ templateId: string; order: number }>;
};

const industryOptions = [
  { value: "general", label: "General" },
  { value: "trucking", label: "Trucking" },
  { value: "service_repair", label: "Service/Repair" },
] as const;

const appliesToOptions = [
  { value: "both", label: "Jobs & Legs" },
  { value: "job", label: "Jobs" },
  { value: "leg", label: "Legs" },
] as const;

const scopeOptions = [
  { value: "company", label: "Company" },
  { value: "system", label: "System" },
] as const;

const triggerOptions = [
  { key: "leg_created", label: "Leg Created", value: { event: "leg_created" as const } },
  { key: "leg_assigned", label: "Leg Assigned", value: { event: "leg_assigned" as const } },
  { key: "leg_status_loaded", label: "Leg Status: Loaded", value: { event: "leg_status" as const, status: "loaded" } },
  { key: "leg_status_in_transit", label: "Leg Status: In Transit", value: { event: "leg_status" as const, status: "intransit" } },
  { key: "leg_status_at_pickup", label: "Leg Status: At Pickup", value: { event: "leg_status" as const, status: "atpickup" } },
  { key: "leg_status_at_delivery", label: "Leg Status: At Delivery", value: { event: "leg_status" as const, status: "atdelivery" } },
  { key: "leg_status_delivered", label: "Leg Status: Delivered", value: { event: "leg_status" as const, status: "delivered" } },
] as const;

const ruleKey = (r: { event: string; status?: string }) =>
  r.event === "leg_status" ? `leg_status:${r.status || ""}` : r.event;

const normalizeTemplateId = (t: any): string => {
  if (!t) return "";
  if (typeof t === "string") return t;
  if (t._id) return String(t._id);
  return "";
};

const GroupsTab: React.FC = () => {
  const { appType } = useConfig();
  const { isSuperAdmin } = usePermissions();
  const canManageScope = isSuperAdmin;
  const canSeeIndustry = isSuperAdmin;

  const { data: groups, isLoading, isError } = useGetTaskTemplateGroupsQuery({
    appType: appType || "all",
  });

  const { data: templates, isLoading: templatesLoading } =
    useGetTaskTemplatesQuery({});

  const [createGroup] = useCreateTaskTemplateGroupMutation();
  const [updateGroup] = useUpdateTaskTemplateGroupMutation();
  const [deleteGroup] = useDeleteTaskTemplateGroupMutation();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ITaskTemplateGroup | null>(
    null
  );

  const [draft, setDraft] = useState<GroupDraft>({
    groupKey: "",
    title: "",
    description: "",
    scope: "company",
    appType: (appType as TaskTemplateAppType) || "all",
    industry: "general",
    appliesTo: "both",
    active: true,
    autoApply: false,
    autoApplyMode: "dedupe",
    triggerRules: [],
    templates: [],
  });

  const templateOptions = useMemo(() => {
    const arr: ITaskTemplate[] = (templates || []) as any;
    const isAllowedForGroup = (t: ITaskTemplate) => {
      // Backwards compatible: missing appliesTo treated as "both"
      const tAppliesTo = (t as any)?.appliesTo || "both";
      if (draft.appliesTo === "both") return true;
      return tAppliesTo === "both" || tAppliesTo === draft.appliesTo;
    };

    return arr
      .filter(isAllowedForGroup)
      .map((t) => ({
        value: t._id,
        label: `${t.title || t.name} (${t.category})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [templates, draft.appliesTo]);

  const startCreate = () => {
    setDraft({
      groupKey: "",
      title: "",
      description: "",
      scope: "company",
      appType: (appType as TaskTemplateAppType) || "all",
      industry: "general",
      appliesTo: "both",
      active: true,
      autoApply: false,
      autoApplyMode: "dedupe",
      triggerRules: [],
      templates: [],
    });
    setShowModal(true);
  };

  const startEdit = (group: ITaskTemplateGroup) => {
    setDraft({
      _id: group._id,
      groupKey: group.groupKey,
      title: group.title,
      description: group.description || "",
      scope: group.scope,
      appType: group.appType,
      industry: group.industry,
      appliesTo: group.appliesTo,
      active: group.active,
      autoApply: !!group.autoApply,
      autoApplyMode: (group.autoApplyMode as any) || "dedupe",
      triggerRules: (group.triggerRules as any) || [],
      templates: (group.templates || [])
        .map((m) => ({
          templateId: normalizeTemplateId(m.templateId),
          order: m.order || 0,
        }))
        .sort((a, b) => a.order - b.order),
    });
    setShowModal(true);
  };

  const moveMember = (idx: number, direction: -1 | 1) => {
    setDraft((prev) => {
      const next = [...prev.templates].sort((a, b) => a.order - b.order);
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      const a = next[idx];
      const b = next[swapIdx];
      const tmp = a.order;
      a.order = b.order;
      b.order = tmp;
      return { ...prev, templates: next.sort((x, y) => x.order - y.order) };
    });
  };

  const addMember = (templateId: string) => {
    if (!templateId) return;
    setDraft((prev) => {
      if (prev.templates.some((m) => m.templateId === templateId)) return prev;
      const maxOrder = prev.templates.reduce((m, x) => Math.max(m, x.order), 0);
      return {
        ...prev,
        templates: [...prev.templates, { templateId, order: maxOrder + 10 }],
      };
    });
  };

  const removeMember = (templateId: string) => {
    setDraft((prev) => ({
      ...prev,
      templates: prev.templates.filter((m) => m.templateId !== templateId),
    }));
  };

  const save = async () => {
    try {
      if (!draft.groupKey.trim() || !draft.title.trim()) {
        toast.error("Group Key and Title are required");
        return;
      }

      const payload = {
        groupKey: draft.groupKey.trim(),
        title: draft.title.trim(),
        description: draft.description?.trim() || "",
        // Scope is restricted to super admins. For non-super-admins:
        // - New groups always default to "company"
        // - Existing groups retain their current scope (e.g., system groups stay system)
        scope: canManageScope ? draft.scope : draft._id ? draft.scope : "company",
        appType: draft.appType,
        ...(canSeeIndustry ? { industry: draft.industry } : {}),
        appliesTo: draft.appliesTo,
        active: draft.active,
        autoApply: draft.autoApply,
        autoApplyMode: draft.autoApplyMode,
        triggerRules: draft.autoApply ? draft.triggerRules : [],
        templates: [...draft.templates]
          .sort((a, b) => a.order - b.order)
          .map((m, idx) => ({ templateId: m.templateId, order: idx * 10 })),
      };

      if (draft._id) {
        await updateGroup({ groupId: draft._id, ...payload }).unwrap();
        toast.success("Group updated");
      } else {
        await createGroup(payload).unwrap();
        toast.success("Group created");
      }

      setShowModal(false);
    } catch (error) {
      console.error("Failed to save group:", error);
      toast.error("Failed to save group");
    }
  };

  const confirmDelete = async () => {
    if (!groupToDelete?._id) return;
    try {
      await deleteGroup(groupToDelete._id).unwrap();
      toast.success("Group deleted");
      setShowDeleteModal(false);
      setGroupToDelete(null);
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error("Failed to delete group");
    }
  };

  const listData = useMemo(() => {
    const arr: ITaskTemplateGroup[] = (groups || []) as any;
    if (!arr.length) {
      return [
        {
          content: (
            <div className="my-10 text-center">
              <Squares2X2Icon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
              <h3 className="mt-2 font-semibold text-sm">No Groups</h3>
              <p className="text-sm text-gray-500">
                Create a group to apply multiple templates in one click
              </p>
            </div>
          ),
        },
      ];
    }

    return arr.map((g) => ({
      title: (
        <div className="flex items-center gap-2">
          <span className="font-medium">{g.title}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
            {g.groupKey}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              g.scope === "system"
                ? "bg-blue-100 text-blue-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {g.scope}
          </span>
          {!g.active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
              inactive
            </span>
          )}
          {g.autoApply && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              auto-apply
            </span>
          )}
        </div>
      ),
      subtitle: (
        <div className="text-sm text-gray-600">
          <div className="flex items-center gap-3">
            <span>Applies to: {g.appliesTo}</span>
            <span>App: {g.appType}</span>
            {canSeeIndustry ? <span>Industry: {g.industry}</span> : null}
            <span>{(g.templates || []).length} templates</span>
            {g.autoApply ? <span>Mode: {g.autoApplyMode || "dedupe"}</span> : null}
          </div>
          {g.description ? <div className="text-gray-500 mt-1">{g.description}</div> : null}
        </div>
      ),
      content: (
        <div className="flex items-center gap-2">
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => startEdit(g)}
            title="Edit Group"
          >
            <PencilIcon className="w-4 h-4 text-gray-500" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => {
              setGroupToDelete(g);
              setShowDeleteModal(true);
            }}
            title="Delete Group"
          >
            <TrashIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      ),
    }));
  }, [groups]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
        <span className="ml-2">Loading groups...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading groups</p>
        <p className="text-sm text-gray-500 mt-1">
          Please check your connection and try again
        </p>
      </div>
    );
  }

  return (
    <>
      <SettingsHeader
        title="Task Template Groups"
        actions={
          <Button size="xs" onClick={startCreate}>
            <PlusIcon className="w-4 h-4 inline mr-1" />
            Add Group
          </Button>
        }
      />

      <SettingsPanel title="Groups" data={listData} />

      {/* Create/Edit Modal */}
      <Modal
        title={draft._id ? "Edit Group" : "Create Group"}
        visible={showModal}
        setVisible={setShowModal}
        onClose={() => setShowModal(false)}
        confirm={save}
        submitText="Save"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Key *
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={draft.groupKey}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, groupKey: e.target.value }))
                }
                placeholder="e.g. trucking_basic"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={draft.title}
                onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Trucking - Basic"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={draft.description}
              onChange={(e) =>
                setDraft((p) => ({ ...p, description: e.target.value }))
              }
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {canManageScope ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scope
                </label>
                <Select
                  value={draft.scope}
                  onChange={(selected: { value: TaskTemplateScope }) =>
                    setDraft((p) => ({ ...p, scope: selected.value }))
                  }
                  options={scopeOptions as any}
                />
              </div>
            ) : null}
            {!canSeeIndustry ? null : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <Select
                  value={draft.industry}
                  onChange={(selected: { value: TaskTemplateIndustry }) =>
                    setDraft((p) => ({ ...p, industry: selected.value }))
                  }
                  options={industryOptions as any}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Applies To
              </label>
              <Select
                value={draft.appliesTo}
                onChange={(selected: { value: TaskTemplateAppliesTo }) =>
                  setDraft((p) => {
                    const nextAppliesTo = selected.value;
                    if (nextAppliesTo === p.appliesTo) return p;

                    // Remove any members that no longer match appliesTo
                    const templateById = new Map(
                      ((templates || []) as any[]).map((t) => [String(t._id), t])
                    );
                    const keep = (p.templates || []).filter((m) => {
                      const t = templateById.get(String(m.templateId));
                      const tAppliesTo = t?.appliesTo || "both";
                      if (nextAppliesTo === "both") return true;
                      return tAppliesTo === "both" || tAppliesTo === nextAppliesTo;
                    });
                    const removed = (p.templates || []).length - keep.length;
                    if (removed > 0) {
                      toast.info(
                        `Removed ${removed} template(s) that don't apply to ${nextAppliesTo}`
                      );
                    }
                    return { ...p, appliesTo: nextAppliesTo, templates: keep };
                  })
                }
                options={appliesToOptions as any}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft((p) => ({ ...p, active: e.target.checked }))}
            />
            <span className="text-sm text-gray-700">Active</span>
          </div>

          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.autoApply}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    autoApply: e.target.checked,
                    triggerRules: e.target.checked ? p.triggerRules : [],
                  }))
                }
              />
              <span className="text-sm text-gray-700">Auto-apply on triggers</span>
            </div>

            {draft.autoApply && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Auto-apply mode
                    </label>
                    <Select
                      value={draft.autoApplyMode}
                      onChange={(selected: { value: "dedupe" | "override" }) =>
                        setDraft((p) => ({ ...p, autoApplyMode: selected.value }))
                      }
                      options={
                        [
                          { value: "dedupe", label: "Dedupe (skip existing tasks)" },
                          { value: "override", label: "Override (replace group tasks)" },
                        ] as any
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Triggers
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {triggerOptions.map((opt) => {
                      const key = ruleKey(opt.value as any);
                      const isChecked = new Set(draft.triggerRules.map(ruleKey)).has(key);
                      return (
                        <label key={opt.key} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setDraft((p) => {
                                const map = new Map(p.triggerRules.map((r) => [ruleKey(r), r]));
                                if (checked) map.set(key, opt.value as any);
                                else map.delete(key);
                                return { ...p, triggerRules: Array.from(map.values()) };
                              });
                            }}
                          />
                          <span className="text-gray-700">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Auto-apply is gated by customer config and only runs where triggers are wired (currently: leg assignment + leg status change).
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-gray-900">Group Templates</div>
              <div className="w-96">
                <Select
                  value={""}
                  onChange={(selected: { value: string }) => addMember(selected.value)}
                  options={templateOptions as any}
                  placeholder={
                    templatesLoading ? "Loading templates..." : "Add template..."
                  }
                  isDisabled={templatesLoading}
                />
              </div>
            </div>

            {draft.templates.length === 0 ? (
              <div className="text-sm text-gray-500">No templates added yet.</div>
            ) : (
              <div className="divide-y border rounded-lg">
                {[...draft.templates]
                  .sort((a, b) => a.order - b.order)
                  .map((m, idx) => {
                    const tpl = (templates as any)?.find?.(
                      (t: ITaskTemplate) => t._id === m.templateId
                    );
                    return (
                      <div
                        key={m.templateId}
                        className="flex items-center justify-between px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {tpl?.title || tpl?.name || m.templateId}
                          </div>
                          <div className="text-xs text-gray-500">
                            order: {idx * 10}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="p-1 hover:bg-gray-100 rounded"
                            onClick={() => moveMember(idx, -1)}
                            title="Move up"
                            disabled={idx === 0}
                          >
                            <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            className="p-1 hover:bg-gray-100 rounded"
                            onClick={() => moveMember(idx, 1)}
                            title="Move down"
                            disabled={idx === draft.templates.length - 1}
                          >
                            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            className="p-1 hover:bg-gray-100 rounded"
                            onClick={() => removeMember(m.templateId)}
                            title="Remove"
                          >
                            <TrashIcon className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        title="Delete Group"
        visible={showDeleteModal}
        setVisible={setShowDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        confirm={confirmDelete}
        submitText="Delete"
      >
        <p>
          Are you sure you want to delete "
          <strong>{groupToDelete?.title}</strong>"?
        </p>
        <p className="text-sm text-gray-500 mt-2">This will mark it inactive.</p>
      </Modal>
    </>
  );
};

export default GroupsTab;


